import Button from '@/components/Base/Button';
import { FormInput } from '@/components/Base/Form';
import { Dialog } from '@/components/Base/Headless';
import Lucide from '@/components/Base/Lucide';
import Table from '@/components/Base/Table';
import { toast } from '@/components/Base/Toast/ToastManager';
import Loading from '@/components/Loading/Loading';
import usePermissions from '@/hooks/usePermissions';
import { useAppDispatch, useAppSelector } from '@/stores/hooks';
import {
  Role,
  cloneRole,
  createRole,
  deleteRole,
  fetchCatalogue,
  fetchRoles,
  setRoleStatus,
  updateRole,
} from '@/stores/rolesSlice';
import { RootState } from '@/stores/store';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import RoleBuilder from './RoleBuilder';

type DialogMode = 'create' | 'edit' | 'view' | 'clone' | null;

const formatStamp = (iso?: string): string =>
  iso
    ? new Date(iso).toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

/** Seeded system roles have no actor — the migration created them. */
const actorName = (actor?: { name?: string; email?: string } | null): string =>
  actor?.name || actor?.email || 'system';

export interface AccessManagementHandle {
  /** Opens the create-role dialog. Lets a parent (e.g. a shared header
   * "New role" button) trigger this from outside the component. */
  openCreate: () => void;
}

interface AccessManagementProps {
  /** Controlled "show archived" state. When provided, the parent owns this
   * value (e.g. a checkbox rendered in a shared header above the tabs). */
  showArchived?: boolean;
  onShowArchivedChange?: (value: boolean) => void;
}

const AccessManagement = forwardRef<
  AccessManagementHandle,
  AccessManagementProps
>(({ showArchived: showArchivedProp, onShowArchivedChange }, ref) => {
  const dispatch = useAppDispatch();
  const { can } = usePermissions();
  const { roles, surfaces, catalogue, isPlatform, loading, saving, error } =
    useAppSelector((state: RootState) => state.roles);

  const [mode, setMode] = useState<DialogMode>(null);
  const [active, setActive] = useState<Role | null>(null);
  const [cloneName, setCloneName] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<Role | null>(null);

  // Falls back to internal state if the parent doesn't control this prop,
  // so the component still works standalone.
  const [showArchivedInternal, setShowArchivedInternal] = useState(false);
  const showArchived = showArchivedProp ?? showArchivedInternal;
  const setShowArchived = onShowArchivedChange ?? setShowArchivedInternal;

  const canManage = can('roles:manage');

  useImperativeHandle(ref, () => ({
    openCreate: () => setMode('create'),
  }));

  useEffect(() => {
    dispatch(fetchRoles(showArchived));
  }, [dispatch, showArchived]);

  useEffect(() => {
    dispatch(fetchCatalogue());
  }, [dispatch]);

  useEffect(() => {
    // Server messages here are written to be read by the user — "3 team
    // members still hold this role" — so surface them verbatim.
    if (error)
      toast.show({
        title: 'Operation failed',
        content: error,
        duration: 4000,
        type: 'warning',
      });
  }, [error]);

  const close = () => {
    setMode(null);
    setActive(null);
    setCloneName('');
  };

  const refresh = () => dispatch(fetchRoles(showArchived));

  const handleSave = async (payload: {
    name: string;
    description: string;
    permissions: string[];
    surfaces: string[];
  }) => {
    const action =
      mode === 'edit' && active
        ? updateRole({ id: active._id, ...payload })
        : createRole(payload);

    const result = await dispatch(action as any);
    if ((result as any).meta.requestStatus === 'fulfilled') {
      toast.show({
        title: mode === 'edit' ? 'Role updated' : 'Role created',
        content: payload.name,
        duration: 2000,
        type: 'primary',
      });
      close();
      refresh();
    }
  };

  const handleClone = async () => {
    if (!active || cloneName.trim().length < 2) return;
    const result = await dispatch(
      cloneRole({ id: active._id, name: cloneName.trim() })
    );
    if (result.meta.requestStatus === 'fulfilled') {
      toast.show({
        title: 'Role cloned',
        content: cloneName.trim(),
        duration: 2000,
        type: 'primary',
      });
      close();
      refresh();
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    const result = await dispatch(deleteRole(confirmDelete._id));
    setConfirmDelete(null);
    if (result.meta.requestStatus === 'fulfilled') {
      toast.show({
        title: 'Role deleted',
        content: confirmDelete.name,
        duration: 2000,
        type: 'primary',
      });
    }
    // On rejection the slice surfaces the server's explanation (e.g. "3 team
    // members still hold this role") via the error toast above.
  };

  const handleStatus = async (role: Role) => {
    const next = role.status === 'active' ? 'inactive' : 'active';
    const result = await dispatch(
      setRoleStatus({ id: role._id, status: next })
    );
    if (result.meta.requestStatus === 'fulfilled') {
      toast.show({
        title: next === 'active' ? 'Role reactivated' : 'Role archived',
        content: role.name,
        duration: 2000,
        type: 'primary',
      });
      refresh();
    }
  };

  return (
    <>
      <div className='flex flex-col box'>
        <div className='overflow-x-auto'>
          {loading ? (
            <Loading />
          ) : (
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Td className="py-4 font-medium border-t bg-slate-50 border-slate-200/60 text-slate-500">
                    Role
                  </Table.Td>
                  <Table.Td className="py-4 font-medium border-t bg-slate-50 border-slate-200/60 text-slate-500">
                    Members
                  </Table.Td>
                  <Table.Td className="py-4 font-medium border-t bg-slate-50 border-slate-200/60 text-slate-500">
                    Status
                  </Table.Td>
                  <Table.Td className="py-4 font-medium border-t bg-slate-50 border-slate-200/60 text-slate-500">
                    Last updated
                  </Table.Td>
                  <Table.Td className="py-4 font-medium text-right border-t bg-slate-50 border-slate-200/60 text-slate-500">
                    Actions
                  </Table.Td>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {roles.length === 0 && (
                  <Table.Tr>
                    <Table.Td
                      colSpan={5}
                      className='py-8 text-center text-sm text-slate-500'
                    >
                      No roles yet.
                    </Table.Td>
                  </Table.Tr>
                )}

                {roles.map((role) => (
                  <Table.Tr key={role._id}>
                    <Table.Td>
                      <div className='flex items-center gap-2'>
                        <span className='font-medium'>{role.name}</span>
                        {role.isSystem && (
                          <span className='rounded bg-slate-200 px-1.5 py-0.5 text-[11px] text-slate-600 dark:bg-darkmode-400 dark:text-slate-300'>
                            System
                          </span>
                        )}
                      </div>
                      {role.description && (
                        <p className='mt-0.5 max-w-md text-xs text-slate-500'>
                          {role.description}
                        </p>
                      )}
                    </Table.Td>
                    <Table.Td>{role.memberCount}</Table.Td>
                    <Table.Td>
                      <span
                        className={
                          role.status === 'active'
                            ? 'text-success'
                            : 'text-slate-400'
                        }
                      >
                        {role.status === 'active' ? 'Active' : 'Archived'}
                      </span>
                    </Table.Td>
                    <Table.Td>
                      <div
                        className='text-xs leading-snug'
                        title={`Created ${formatStamp(role.createdAt)} by ${actorName(role.createdBy)}`}
                      >
                        <div>{formatStamp(role.updatedAt)}</div>
                        <div className='text-slate-500'>
                          by {actorName(role.updatedBy)}
                        </div>
                      </div>
                    </Table.Td>
                    <Table.Td className='text-right'>
                      <div className='flex justify-end gap-2 text-xs'>
                        <button
                          className='text-primary hover:underline'
                          onClick={() => {
                            setActive(role);
                            setMode(role.editable ? 'edit' : 'view');
                          }}
                        >
                          {role.editable ? 'Edit' : 'View'}
                        </button>

                        {canManage && (
                          <button
                            className='text-primary hover:underline'
                            onClick={() => {
                              setActive(role);
                              setCloneName(`${role.name} (copy)`);
                              setMode('clone');
                            }}
                          >
                            Clone
                          </button>
                        )}

                        {canManage && role.editable && (
                          <button
                            className='text-slate-500 hover:underline'
                            onClick={() => handleStatus(role)}
                          >
                            {role.status === 'active' ? 'Archive' : 'Restore'}
                          </button>
                        )}

                        {canManage && role.editable && (
                          <button
                            className={
                              role.memberCount > 0
                                ? 'cursor-not-allowed text-slate-300'
                                : 'text-danger hover:underline'
                            }
                            disabled={role.memberCount > 0}
                            // Disabled with a reason rather than letting the
                            // user discover the 409 after clicking.
                            title={
                              role.memberCount > 0
                                ? `${role.memberCount} member(s) still hold this role`
                                : 'Delete role'
                            }
                            onClick={() => setConfirmDelete(role)}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </div>
      </div>

      {/* Create / edit / view */}
      <Dialog
        open={mode === 'create' || mode === 'edit' || mode === 'view'}
        onClose={close}
        size='xl'
      >
        <Dialog.Panel className='p-6'>
          <h3 className='mb-4 text-base font-medium'>
            {mode === 'create'
              ? 'New role'
              : mode === 'edit'
                ? `Edit ${active?.name}`
                : active?.name}
          </h3>
          <RoleBuilder
            // Remount per target: the builder seeds its state from `role` in
            // useState initialisers, which would otherwise keep the previous
            // role's selections when the dialog is reopened on another one.
            key={`${mode}-${active?._id ?? 'new'}`}
            surfaces={surfaces}
            catalogue={catalogue}
            isPlatform={isPlatform}
            role={mode === 'create' ? null : active}
            readOnly={mode === 'view'}
            saving={saving}
            onCancel={close}
            onSave={handleSave}
          />
        </Dialog.Panel>
      </Dialog>

      {/* Clone */}
      <Dialog open={mode === 'clone'} onClose={close}>
        <Dialog.Panel className='p-6'>
          <h3 className='mb-1 text-base font-medium'>Clone {active?.name}</h3>
          <p className='mb-4 text-xs text-slate-500'>
            Creates an editable copy with the same access.
          </p>
          <FormInput
            type='text'
            value={cloneName}
            placeholder='New role name'
            onChange={(e) => setCloneName(e.target.value)}
          />
          <div className='mt-5 flex justify-end gap-2'>
            <Button variant='outline-secondary' onClick={close}>
              Cancel
            </Button>
            <Button
              variant='primary'
              disabled={cloneName.trim().length < 2 || saving}
              onClick={handleClone}
            >
              {saving ? 'Cloning…' : 'Clone role'}
            </Button>
          </div>
        </Dialog.Panel>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <Dialog.Panel className='p-6'>
          <div className='text-center'>
            <Lucide icon='XCircle' className='mx-auto h-12 w-12 text-danger' />
            <h3 className='mt-4 text-base font-medium'>
              Delete “{confirmDelete?.name}”?
            </h3>
            <p className='mt-2 text-xs text-slate-500'>
              This role is not assigned to anyone. It will be removed from the
              list and its name freed for reuse. You cannot undo this here — if
              you may want the role back, use Archive instead, which keeps it
              visible and can be reversed.
            </p>
          </div>
          <div className='mt-5 flex justify-center gap-2'>
            <Button
              variant='outline-secondary'
              onClick={() => setConfirmDelete(null)}
            >
              Cancel
            </Button>
            <Button variant='danger' disabled={saving} onClick={handleDelete}>
              {saving ? 'Deleting…' : 'Delete'}
            </Button>
          </div>
        </Dialog.Panel>
      </Dialog>
    </>
  );
});

AccessManagement.displayName = 'AccessManagement';

export default AccessManagement;